import type { DiscoverySummary } from '@/lib/api/discovery';
import type {
  AddSuggestionsResult,
  Suggestion,
  SuggestionDismissReason,
  Suggestions,
} from '@/lib/api/discovery-suggestions';
import { formatDay, plural } from '@/components/discovery/discovery-logic';

/**
 * Pure helpers behind Add from discovery (T-0190). They word what the API
 * computed (support, confidence, matches, numbers); nothing here counts.
 * Design reference: `requirementsView` and the `sug-add` / `sug-dismiss`
 * dialogs in `docs/discovery/prototype/discovery.js`.
 */

/** Its selection, kept on the server: confident ones arrive picked. */
export const isPicked = (s: Suggestion) => s.picked;

/** Only one person stands behind it so far (Decision 54.36): shown as a caution. */
export const onePersonSoFar = (s: Pick<Suggestion, 'support'>) =>
  s.support.people === 1 && s.support.documents === 0;

/** Who and what supports it, from its own confirmed evidence. */
export function supportText(s: Pick<Suggestion, 'support'>): string {
  const { people, documents } = s.support;
  if (people === 0 && documents === 0) return 'No confirmed evidence left.';
  const parts: string[] = [];
  if (people) parts.push(plural(people, 'person', 'people'));
  if (documents) parts.push(plural(documents, 'document'));
  return `Supported by ${parts.join(' and ')}.`;
}

/**
 * The run line. The prototype's "read sessions … in full" is replaced: a
 * run reads confirmed evidence only, and says how much.
 */
export function lastRunText(d: Pick<Suggestions, 'last_run'>): string {
  const head = 'Built from confirmed evidence only, and every suggestion cites it.';
  const r = d.last_run;
  if (!r) return `${head} The first run starts by itself once evidence is first confirmed.`;
  const cost = `$${(r.cost_cents / 100).toFixed(2)}`;
  return `${head} Last run ${formatDay(r.at)}${r.by ? ` by ${r.by.name}` : ''} · read ${plural(r.evidence_read, 'confirmed evidence item')} · AI cost ${cost} of the engagement’s AI budget.`;
}

export function awaitingText(n: number): string | null {
  if (n <= 0) return null;
  return `${plural(n, 'evidence item')} awaiting review ${n === 1 ? 'is' : 'are'} not used yet. Confirm ${n === 1 ? 'it' : 'them'}, then run again.`;
}

/** The open ones that are picked, split into what is added and what only links evidence. */
export function pickedSummary(open: ReadonlyArray<Suggestion>) {
  const picked = open.filter(isPicked);
  const toLink = picked.filter((s) => s.match).length;
  return { picked, toAdd: picked.length - toLink, toLink };
}

/** The one line each picked suggestion gets in the add dialog. */
export function addLine(s: Suggestion): { verb: string; number: string } {
  if (s.match) return { verb: 'Link evidence', number: s.match.number };
  if (s.library_item) return { verb: 'Add', number: s.library_item.number };
  return { verb: 'Add new', number: s.lands_in.number_preview };
}

/** "Accounts Payable · keeps AP-006" / "· gets AP-C01 or the next free number". */
export function landsInText(s: Suggestion): string {
  const m = s.lands_in.module.name;
  if (s.match) return `${m} · links to ${s.match.number}`;
  if (s.library_item) return `${m} · keeps ${s.library_item.number}`;
  return `${m} · gets ${s.lands_in.number_preview} or the next free number`;
}

export function matchText(s: Suggestion): string | null {
  if (!s.match) return null;
  return s.match.status === 'removed'
    ? `Matches ${s.match.number}, which was removed from this engagement’s requirements. Adding restores it with its number and links this evidence to it.`
    : `Matches ${s.match.number}, already in this engagement’s requirements. Adding links this evidence to it as another citation. Its number and original citation stay as they are.`;
}

export const DISMISS_LABEL: Record<SuggestionDismissReason, string> = {
  out_of_scope: 'Out of scope',
  already_covered: 'Already covered by another requirement',
  not_what_they_meant: 'Not what they meant',
  other: 'Other',
};

export function dismissedText(s: Suggestion): string {
  const r = s.dismiss_reason as SuggestionDismissReason | null | undefined;
  if (!r) return 'Dismissed';
  return r === 'other' && s.dismiss_text ? s.dismiss_text : (DISMISS_LABEL[r] ?? r);
}

/** The toast after adding (rule 23: one requirement per suggestion, linked when it matches). */
export function addedToast(r: AddSuggestionsResult): string {
  const parts: string[] = [];
  if (r.added.length) {
    parts.push(
      `${plural(r.added.length, 'requirement')} added to Selection with discovery citations (${r.added.map((a) => a.number).join(', ')}).`,
    );
  }
  if (r.linked.length) {
    const restored = r.linked.filter((l) => l.restored).map((l) => l.number);
    parts.push(
      `Evidence linked to ${r.linked.map((l) => l.number).join(', ')}.${restored.length ? ` ${restored.join(', ')} restored.` : ''}`,
    );
  }
  if (r.skipped.length) {
    parts.push(`${plural(r.skipped.length, 'suggestion')} skipped: already decided.`);
  }
  return parts.join(' ') || 'Nothing to add.';
}

/** The sign-off state in two or three words, for the header (Decision 54.40). */
export function signoffWords(s: DiscoverySummary | undefined): string | null {
  const item = s?.attention.find((a) => a.kind === 'signoff');
  if (!item) return null;
  const named: Record<string, string> = {
    signed: 'Signed',
    waived: 'Waived',
    requested: 'Awaiting signature',
    changes_requested: 'Changes requested',
    reopened: 'Reopened',
  };
  if (item.state && named[item.state]) return named[item.state]!;
  const open = item.counts['open_conditions'] ?? 0;
  const total = item.counts['conditions'] ?? 0;
  return open > 0 ? `${open} of ${total} conditions open` : 'Ready to request';
}
